import React, { useState } from 'react';
import { Badge, Box, Stack } from '@mui/material';
import { TabsWrapper } from 'framework/ui/TabsWrapper';
import { tabBadgeSx, tabsActionsSlotSx, tabsSx, tabSx } from './styles';

export interface PageContentProcessTabItem {
  id: string;
  name: string;
  badge?: number;
  panel: React.ReactNode;
  actions?: React.ReactNode[];
}

export interface PageContentProcessTabsProps {
  tabs?: PageContentProcessTabItem[];
  actions?: React.ReactNode[];
  activeTabId?: string;
  defaultActiveTabId?: string;
  onActiveTabChange?: (tabId: string) => void;
}

const TabsActionsSlot = ({ children }: { children: React.ReactNode[] }) => (
  <Stack direction="row" alignItems="center" spacing={2} useFlexGap sx={tabsActionsSlotSx}>
    {React.Children.toArray(children)}
  </Stack>
);

const PageContentProcessTabs: React.FC<PageContentProcessTabsProps> = ({
  tabs,
  actions,
  activeTabId: controlledActiveTabId,
  defaultActiveTabId,
  onActiveTabChange,
}) => {
  const [internalActiveTabId, setInternalActiveTabId] = useState(defaultActiveTabId);

  if (!tabs?.length) return null;

  const requestedActiveTabId = controlledActiveTabId ?? internalActiveTabId;
  const activeTabIndex = Math.max(
    tabs.findIndex(tab => tab.id === requestedActiveTabId),
    0,
  );
  const visibleActions = (tabs[activeTabIndex].actions ?? actions)?.filter(Boolean);

  const handleTabChange = (index: number) => {
    const tab = tabs[index];
    if (!tab) return;

    if (controlledActiveTabId === undefined) {
      setInternalActiveTabId(tab.id);
    }
    onActiveTabChange?.(tab.id);
  };

  return (
    <Box>
      <TabsWrapper
        tabs={tabs.map((tab, index) => ({
          value: index,
          label:
            tab?.badge !== undefined ? (
              <>
                {tab.name}
                <Badge badgeContent={tab.badge} color="primary" sx={tabBadgeSx} />
              </>
            ) : (
              tab.name
            ),
          content: tab.panel,
        }))}
        buttons={
          visibleActions?.length ? [<TabsActionsSlot key="actions">{visibleActions}</TabsActionsSlot>] : undefined
        }
        buttonsPlacement="end"
        defaultTab={activeTabIndex}
        changeTabCallback={handleTabChange}
        sxContentProps={{ p: 0 }}
        sxTab={tabSx}
        sxTabs={tabsSx}
        wrapButtons
      />
    </Box>
  );
};

export default PageContentProcessTabs;
